import { useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCardMachines, useDeleteCardMachine } from './useCardMachines';
import { CardMachineModal } from './CardMachineModal';
import type { CardMachine } from './types';

function pct(n: number): string {
  return `${Number(n).toString().replace('.', ',')}%`;
}

export function MachinesList() {
  const { data: machines = [], isLoading } = useCardMachines();
  const remove = useDeleteCardMachine();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CardMachine | undefined>(undefined);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const openNew = () => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (m: CardMachine) => {
    setEditing(m);
    setModalOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button
          onClick={openNew}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={14} className="mr-2" />
          Nova maquininha
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : machines.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <CreditCard className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Nenhuma maquininha cadastrada.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={openNew}>
              <Plus size={14} className="mr-1.5" />
              Cadastrar primeira
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {machines.map((m) => (
            <Card key={m.id} className={m.is_active ? '' : 'opacity-60'}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate font-semibold text-foreground">{m.name}</h4>
                    {m.operator && <p className="text-xs text-muted-foreground">{m.operator}</p>}
                  </div>
                  {!m.is_active && (
                    <span className="rounded bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                      Inativa
                    </span>
                  )}
                </div>

                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <span className="text-muted-foreground">Débito</span>
                  <span className="text-right tabular-nums">{pct(m.mdr_debit_pct)}</span>
                  <span className="text-muted-foreground">Crédito 1×</span>
                  <span className="text-right tabular-nums">{pct(m.mdr_credit_pct)}</span>
                  <span className="text-muted-foreground">Parcelado</span>
                  <span className="text-right tabular-nums">{pct(m.mdr_installments_pct)}</span>
                  <span className="text-muted-foreground">Antecipação</span>
                  <span className="text-right tabular-nums">
                    {pct(m.anticipation_rate_monthly_pct)}/m
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-end gap-1 border-t border-border pt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(m)}
                    className="h-7 px-2 text-xs"
                  >
                    <Pencil size={12} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmId(m.id)}
                    disabled={remove.isPending}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-expense"
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CardMachineModal open={modalOpen} onOpenChange={setModalOpen} edit={editing} />

      <AlertDialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir maquininha?</AlertDialogTitle>
            <AlertDialogDescription>
              Antecipações já registradas com essa maquininha continuam no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmId) remove.mutate(confirmId);
                setConfirmId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
